/* LLCL / Bag V2.1. Presentation only: no cart write or price calculation.
 * Recommendations come exclusively from Shopify or the merchant's curated list.
 */
let carouselSequence = 0;
class LLBagCarousel extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    if (this.querySelector('[data-bag-track]')) { this.setup(); return; }
    const load = () => { this.intersection?.disconnect(); this.load(); };
    if ('IntersectionObserver' in window) {
      this.intersection = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) load(); }, { rootMargin: '600px' });
      this.intersection.observe(this.previousElementSibling || this);
    } else load();
  }
  disconnectedCallback() {
    this.abort?.abort(); this.abort = null;
    this.intersection?.disconnect(); this.resize?.disconnect(); this.autoplayObserver?.disconnect();
    clearTimeout(this.autoplayTimer); clearTimeout(this.autoplayResumeTimer);
    cancelAnimationFrame(this.frame);
    const track = this.querySelector('[data-bag-track]');
    if (track) delete track.dataset.ready;
  }
  filterCards(content) {
    // Recommendation rendering may omit cart context. Use this page's real,
    // server-rendered Shopify line IDs as the final exclusion boundary.
    const page = this.closest('[data-ll-bag-page]') || document;
    const selected = new Set([...page.querySelectorAll('[data-bag-key][data-product-id]')].map(row => row.dataset.productId));
    const seen = new Set();
    for (const card of content.querySelectorAll('[data-recommended-product]')) {
      const id = card.dataset.recommendedProduct;
      if (!id || selected.has(id) || seen.has(id) || seen.size >= 6) card.remove();
      else seen.add(id);
    }
    return seen.size;
  }
  async load() {
    const controller = this.abort;
    if (!controller || this.loading || !this.dataset.endpoint || !this.dataset.productId) return;
    this.loading = true;
    try {
      for (const intent of ['complementary', 'related']) {
        const url = new URL(this.dataset.endpoint, location.origin);
        if (url.origin !== location.origin) throw new Error('Unexpected recommendation origin');
        url.search = new URLSearchParams({ section_id: 'll-bag-recommendations', product_id: this.dataset.productId, intent, limit: '10' });
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Recommendations unavailable');
        const content = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-bag-recommendations-content]');
        if (!content || !this.filterCards(content)) continue;
        if (!this.isConnected || controller.signal.aborted) return;
        this.replaceChildren(document.importNode(content, true));
        this.dataset.intent = intent; this.hidden = false; this.setup(); return;
      }
      this.hidden = true;
    } catch (error) {
      if (error.name !== 'AbortError') this.hidden = true;
    } finally { this.loading = false; }
  }
  setup() {
    const track = this.querySelector('[data-bag-track]');
    if (!track || !this.abort || track.dataset.ready) return;
    if (!this.filterCards(track)) { this.hidden = true; return; }
    track.dataset.ready = 'true';
    track.id = `ll-bag-suggestions-${++carouselSequence}`;
    const controls = this.querySelector('.ll-bag-carousel__controls');
    const previous = this.querySelector('[data-bag-prev]');
    const next = this.querySelector('[data-bag-next]');
    for (const button of [previous, next]) button?.setAttribute('aria-controls', track.id);
    const current = this.querySelector('[data-bag-current]');
    const total = this.querySelector('[data-bag-total]');
    const progress = this.querySelector('[data-bag-progress]');
    const cards = [...track.querySelectorAll('.ll-bag-recommendation')];
    if (total) total.textContent = String(cards.length).padStart(2, '0');
    const sync = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const end = Math.max(0, track.scrollWidth - track.clientWidth);
        if (controls) controls.hidden = end < 2;
        if (previous) previous.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= end - 2;
        const left = Math.max(0, Math.min(track.scrollLeft, end));
        const step = (cards[0]?.getBoundingClientRect().width || track.clientWidth) + (parseFloat(getComputedStyle(track).columnGap) || 0);
        if (current) current.textContent = String(Math.min(cards.length, Math.round(left / step) + 1)).padStart(2, '0');
        if (progress) progress.style.transform = `scaleX(${Math.min(1, (left + track.clientWidth) / Math.max(1, track.scrollWidth))})`;
      });
    };
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    let autoplayVisible = !('IntersectionObserver' in window);
    let autoplayInteracting = false;
    const autoDelay = 3800;
    const firstDelay = 2200;
    const resumeDelay = 5200;

    const move = direction => {
      const card = track.querySelector('.ll-bag-recommendation');
      const step = (card?.getBoundingClientRect().width || track.clientWidth) + (parseFloat(getComputedStyle(track).columnGap) || 0);
      const visible = Math.max(1, Math.floor(track.clientWidth / step));
      track.scrollBy({ left: direction * step * visible, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    };

    const moveAuto = () => {
      const card = track.querySelector('.ll-bag-recommendation');
      const step = (card?.getBoundingClientRect().width || track.clientWidth) + (parseFloat(getComputedStyle(track).columnGap) || 0);
      const end = Math.max(0, track.scrollWidth - track.clientWidth);
      if (end < 2) return;
      const atEnd = track.scrollLeft >= end - Math.max(3, step * .08);
      if (atEnd) track.scrollTo({ left: 0, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
      else track.scrollBy({ left: step, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    };

    const canAutoplay = () => !reducedMotion.matches && !document.hidden && autoplayVisible && !autoplayInteracting && track.scrollWidth - track.clientWidth > 2;
    const stopAutoplay = () => clearTimeout(this.autoplayTimer);
    const scheduleAutoplay = (delay = autoDelay) => {
      stopAutoplay();
      if (!canAutoplay()) return;
      this.autoplayTimer = window.setTimeout(() => {
        if (!canAutoplay()) return;
        moveAuto();
        scheduleAutoplay();
      }, delay);
    };
    const pauseAutoplay = (delay = resumeDelay) => {
      stopAutoplay();
      clearTimeout(this.autoplayResumeTimer);
      autoplayInteracting = true;
      this.autoplayInteracting = true;
      this.autoplayResumeTimer = window.setTimeout(() => {
        autoplayInteracting = false;
        this.autoplayInteracting = false;
        scheduleAutoplay();
      }, delay);
    };
    const holdAutoplay = () => {
      clearTimeout(this.autoplayResumeTimer);
      autoplayInteracting = true;
      this.autoplayInteracting = true;
      stopAutoplay();
    };
    const releaseAutoplay = () => {
      autoplayInteracting = false;
      this.autoplayInteracting = false;
      scheduleAutoplay(resumeDelay);
    };

    const options = { signal: this.abort.signal };
    previous?.addEventListener('click', () => { pauseAutoplay(); move(-1); }, options);
    next?.addEventListener('click', () => { pauseAutoplay(); move(1); }, options);
    track.addEventListener('scroll', sync, { ...options, passive: true });
    track.addEventListener('pointerdown', () => pauseAutoplay(), { ...options, passive: true });
    track.addEventListener('wheel', () => pauseAutoplay(), { ...options, passive: true });
    this.addEventListener('focusin', holdAutoplay, options);
    this.addEventListener('focusout', event => {
      if (!this.contains(event.relatedTarget)) releaseAutoplay();
    }, options);
    track.addEventListener('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault(); pauseAutoplay(); move(event.key === 'ArrowRight' ? 1 : -1);
    }, options);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopAutoplay();
      else scheduleAutoplay(resumeDelay);
    }, options);
    reducedMotion.addEventListener?.('change', () => {
      stopAutoplay();
      if (!reducedMotion.matches) scheduleAutoplay(resumeDelay);
    }, options);

    if ('IntersectionObserver' in window) {
      this.autoplayObserver = new IntersectionObserver(entries => {
        autoplayVisible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .12);
        if (autoplayVisible) scheduleAutoplay(firstDelay);
        else stopAutoplay();
      }, { threshold: [.12] });
      this.autoplayObserver.observe(track);
    } else {
      autoplayVisible = true;
      scheduleAutoplay(firstDelay);
    }

    this.resize = new ResizeObserver(() => { sync(); if (autoplayVisible) scheduleAutoplay(); });
    this.resize.observe(track);
    sync();
  }
}
if (!customElements.get('ll-bag-carousel')) customElements.define('ll-bag-carousel', LLBagCarousel);
const initializedServices = new WeakSet();
function setupServices() {
  const services = document.querySelector('.ll-bag-services');
  if (!services || initializedServices.has(services)) return;
  initializedServices.add(services);
  services.addEventListener('click', event => {
    const close = event.target instanceof Element ? event.target.closest('[data-bag-service-close]') : null;
    if (!close) return;
    const details = close.closest('details'); details.open = false; details.querySelector('summary')?.focus({ preventScroll: true });
  });
  customElements.whenDefined('country-selector').then(() => {
    const selector = services.querySelector('country-selector');
    const country = selector?.querySelector('[name="address[country]"]');
    const province = selector?.querySelector('[name="address[province]"]');
    if (!country || !province || !services.isConnected) return;
    const es = services.closest('[data-language]')?.dataset.language === 'es';
    const sync = () => {
      const provinces = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]');
      province.disabled = !provinces.length; province.required = false;
      if (!provinces.length) { province.replaceChildren(); return; }
      if (province.options[0]?.value !== '') {
        const known = selector.getAttribute('province');
        const sameCountry = country.selectedOptions[0].textContent === selector.getAttribute('country');
        province.insertBefore(new Option(es ? 'Selecciona un estado' : 'Choose a state', '', false, !known || !sameCountry), province.firstChild);
      }
    };
    country.addEventListener('change', sync); sync();
    const button = services.querySelector('.ll-bag-service__calculate');
    button?.addEventListener('click', event => {
      province.required = !province.disabled;
      const valid = province.disabled || province.reportValidity();
      province.required = false;
      if (button.getAttribute('aria-busy') === 'true' || !valid) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
    }, true);
  });
}
setupServices();
document.addEventListener('shopify:section:load', setupServices);
